import React, { useContext, useEffect, useRef, useState } from "react";
import "./onboardingProgressBar.css";
import { CombinedContext } from "../../../context/combinedContext";
import { ChevronDown } from "lucide-react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import { useNavigate } from "react-router-dom";
import translations from "../../../translations/translations";

/* ─────────────────────────────────────────
   STEP CONFIG (NO TRANSLATED STRINGS HERE)
────────────────────────────────────────── */
const STEP_CONFIG = {
	schedule: {
		id: "baseSchedule",
		labelKey: "schedule",
		page: "/calendar",
	},
	services: {
		id: "services",
		labelKey: "services",
		page: "/staff/profile",
	},
};

const OnboardingProgressBarStaff = () => {
	const { userData, language } = useContext(CombinedContext);
	const navigate = useNavigate();

	const user = userData?.user;
	const staffSetup = userData?.user?.onboarding;

	const [showDetails, setShowDetails] = useState(true);
	const [launching, setLaunching] = useState(false);
	const [message, setMessage] = useState(null);
	const [showBubble, setShowBubble] = useState(true);

	const listRef = useRef(null);

	// Scroll to top on mount when setup is incomplete
	useEffect(() => {
		if (!staffSetup?.isComplete) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}, []);

	useEffect(() => {
		const el = listRef.current;
		if (!el) return;

		if (showDetails) {
			el.style.height = el.scrollHeight + "px";
			el.style.opacity = "1";
			el.style.transform = "translateY(0)";

			const handleTransitionEnd = () => {
				el.style.height = "auto";
				el.removeEventListener("transitionend", handleTransitionEnd);
			};
			el.addEventListener("transitionend", handleTransitionEnd);
		} else {
			el.style.height = el.scrollHeight + "px";
			requestAnimationFrame(() => {
				el.style.height = "0px";
				el.style.opacity = "0";
				el.style.transform = "translateY(-8px)";
			});
		}
	}, [showDetails]);

	if (!user || user.role !== "staff" || !staffSetup) return null;

	const steps = staffSetup.steps || {};
	const requiredSteps = staffSetup.requiredSteps || Object.keys(steps);

	const totalSteps = requiredSteps.length;
	const completedSteps = requiredSteps.filter((step) => steps[step]).length;
	const percentage = Math.round((completedSteps / totalSteps) * 100);

	const allComplete = staffSetup.isComplete;
	const isActive = user.status === "active";

	const goToStep = (step) => {
		const stepObj = STEP_CONFIG[step];
		if (!stepObj) return;

		navigate(stepObj.page, {
			state: { openModal: stepObj.id },
		});
	};

	const handleLaunchStaff = async () => {
		try {
			setLaunching(true);
			setMessage(null);

			const res = await companyBasedRequest.launchStaff(user.userId, userData.cognitoTokens?.AccessToken);

			window.location.href = "/calendar?showLiveBannerStaff=true";
		} catch (err) {
			console.error("Launch failed:", err);
			setMessage({
				type: "error",
				text: translations[language].onboarding.messages.error || "Failed to launch staff profile.",
			});
		} finally {
			setLaunching(false);
		}
	};

	const toggleDetails = () => {
		setShowDetails((prev) => !prev);
	};

	return (
		<div>
			<div className="onboard-progress-wrapper">
				{/* HEADER */}
				<div className="onboard-progress-top">
					<div className="onboard-banner-titles">
						<div className="onboard-banner-title">{translations[language].onboarding.staff.bannerTitle || "Complete your staff profile"}</div>
						<div className="onboard-banner-subtitle">
							{completedSteps} / {totalSteps} {translations[language].onboarding.stepsCompleted} —{" "}
							{translations[language].onboarding.staff.bannerSubtitle || "finish to start accepting bookings"}
						</div>
					</div>

					<button className="onboard-toggle-btn" onClick={toggleDetails}>
						<span className="onboard-toggle-text">
							{showDetails ? translations[language].onboarding.hideSteps : translations[language].onboarding.showSteps}
						</span>
						<ChevronDown size={16} className={`onboard-chevron ${showDetails ? "rotate" : ""}`} />
					</button>
				</div>

				{/* PROGRESS BAR */}
				<div className="onboard-progress-bar">
					<div className="onboard-progress-fill" style={{ width: `${percentage}%` }} />
				</div>

				{/* STEP LIST */}
				<ul ref={listRef} className="onboard-steps-list">
					{requiredSteps.map((step) => {
						const labelKey = STEP_CONFIG[step]?.labelKey;

						return (
							<li key={step} className={`onboard-step-item ${steps[step] ? "done" : "pending"}`} onClick={() => goToStep(step)}>
								<span className={`step-indicator ${steps[step] ? "completed" : "pending"}`} />
								<span className="step-name">{translations[language].onboarding.staff.steps[labelKey] || step}</span>
							</li>
						);
					})}
				</ul>

				{/* LAUNCH STAFF BUTTON */}
				{allComplete && !isActive && (
					<div className="onboard-launch-section">
						<button className="onboard-launch-btn" onClick={handleLaunchStaff} disabled={launching}>
							{launching ? translations[language].onboarding.staff.launching : translations[language].onboarding.staff.launch}
						</button>

						{message && <p className={`onboard-msg ${message.type}`}>{message.text}</p>}
					</div>
				)}
			</div>

			{/* BUBBLE */}
			{showBubble && (
				<div className="onboard-overlay-bubble-div">
					<div className="onboard-overlay-bubble">
						<div className="onboard-overlay-arrow" />
						<div className="onboard-overlay-content">
							<div className="onboard-overlay-title-div">
								<strong>{translations[language].onboarding?.hintTitle || "Complete your setup"}</strong>
								<button className="onboard-overlay-close" onClick={() => setShowBubble(false)} aria-label="Close">
									×
								</button>
							</div>
							<p>{translations[language].onboarding?.hintText || "Finish these steps to activate your shop and start accepting bookings."}</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default OnboardingProgressBarStaff;
